import type {
  CanonicalTreeRetirementReadinessClientEvidenceKind,
} from './CanonicalTreeHistoricalAuthorityPresentationObservabilityLifecycleRetirementReadinessAuthenticatedEvidencePublicationArchitecture';

export type CanonicalTreeAuthenticatedEvidenceProductionObservationStatus =
  | 'ACCEPTED'
  | 'DUPLICATE_ACCEPTED';

export interface CanonicalTreeAuthenticatedEvidenceProductionObservation {
  readonly eventKind:
    CanonicalTreeRetirementReadinessClientEvidenceKind;

  readonly status:
    CanonicalTreeAuthenticatedEvidenceProductionObservationStatus;

  readonly duplicate:
    boolean;

  readonly evidenceId:
    string;

  readonly receivedAt:
    string;

  readonly buildId:
    string;

  readonly deploymentId:
    string;

  readonly installationId:
    string;

  readonly retirementAuthorized:
    false;

  readonly observedAt:
    string;
}

export interface CanonicalTreeAuthenticatedEvidenceProductionObservationRegistrySnapshot {
  readonly milestone:
    'CC-TREE-ARCH-105';

  readonly registry:
    'AUTHENTICATED_EVIDENCE_PRODUCTION_OBSERVATION_REGISTRY';

  readonly scope:
    'TAB_MEMORY_ONLY';

  readonly latestObservation:
    CanonicalTreeAuthenticatedEvidenceProductionObservation | null;

  readonly observationCount:
    number;

  readonly revision:
    number;

  readonly changedAt:
    string | null;

  readonly containsSecrets:
    false;

  readonly containsAuthenticationData:
    false;

  readonly containsOwnerIdentity:
    false;

  readonly containsPrivateFamilyData:
    false;

  readonly containsRawTreeData:
    false;

  readonly containsRpcError:
    false;

  readonly productionIngestionCertified:
    false;

  readonly retirementAuthorized:
    false;
}

export type CanonicalTreeAuthenticatedEvidenceProductionObservationRegistryListener =
  () => void;

export type CanonicalTreeAuthenticatedEvidenceProductionObservationRegistryUnsubscribe =
  () => void;

export interface CanonicalTreeAuthenticatedEvidenceProductionObservationRegistry {
  readonly getSnapshot:
    () => CanonicalTreeAuthenticatedEvidenceProductionObservationRegistrySnapshot;

  readonly subscribe: (
    listener:
      CanonicalTreeAuthenticatedEvidenceProductionObservationRegistryListener,
  ) => CanonicalTreeAuthenticatedEvidenceProductionObservationRegistryUnsubscribe;

  readonly publishAccepted: (
    observation:
      CanonicalTreeAuthenticatedEvidenceProductionObservation,
  ) => CanonicalTreeAuthenticatedEvidenceProductionObservationRegistrySnapshot;

  readonly clear:
    () => CanonicalTreeAuthenticatedEvidenceProductionObservationRegistrySnapshot;
}

const createInitialSnapshot =
  (): CanonicalTreeAuthenticatedEvidenceProductionObservationRegistrySnapshot =>
    Object.freeze({
      milestone:
        'CC-TREE-ARCH-105',

      registry:
        'AUTHENTICATED_EVIDENCE_PRODUCTION_OBSERVATION_REGISTRY',

      scope:
        'TAB_MEMORY_ONLY',

      latestObservation:
        null,

      observationCount:
        0,

      revision:
        0,

      changedAt:
        null,

      containsSecrets:
        false,

      containsAuthenticationData:
        false,

      containsOwnerIdentity:
        false,

      containsPrivateFamilyData:
        false,

      containsRawTreeData:
        false,

      containsRpcError:
        false,

      productionIngestionCertified:
        false,

      retirementAuthorized:
        false,
    });

const isValidDate =
  (
    value:
      string,
  ): boolean =>
    !Number.isNaN(
      Date.parse(
        value,
      ),
    );

const isValidUuid =
  (
    value:
      string,
  ): boolean =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(
      value,
    );

const validateObservation =
  (
    observation:
      CanonicalTreeAuthenticatedEvidenceProductionObservation,
  ): CanonicalTreeAuthenticatedEvidenceProductionObservation => {
    if (
      (
        observation.status !==
          'ACCEPTED' &&
        observation.status !==
          'DUPLICATE_ACCEPTED'
      ) ||
      observation.duplicate !==
        (
          observation.status ===
            'DUPLICATE_ACCEPTED'
        ) ||
      !isValidUuid(
        observation.evidenceId,
      ) ||
      !isValidUuid(
        observation.installationId,
      ) ||
      !isValidDate(
        observation.receivedAt,
      ) ||
      !isValidDate(
        observation.observedAt,
      ) ||
      observation.buildId.trim().length <
        1 ||
      observation.buildId.trim().length >
        200 ||
      observation.deploymentId.trim().length <
        1 ||
      observation.deploymentId.trim().length >
        200 ||
      observation.retirementAuthorized !==
        false
    ) {
      throw new Error(
        'INVALID_SANITIZED_PRODUCTION_OBSERVATION',
      );
    }

    return Object.freeze({
      eventKind:
        observation.eventKind,

      status:
        observation.status,

      duplicate:
        observation.duplicate,

      evidenceId:
        observation.evidenceId,

      receivedAt:
        observation.receivedAt,

      buildId:
        observation.buildId.trim(),

      deploymentId:
        observation.deploymentId.trim(),

      installationId:
        observation.installationId,

      retirementAuthorized:
        false,

      observedAt:
        observation.observedAt,
    });
  };

export const createCanonicalTreeAuthenticatedEvidenceProductionObservationRegistry =
  (
    now:
      () => string =
      () => new Date().toISOString(),
  ): CanonicalTreeAuthenticatedEvidenceProductionObservationRegistry => {
    let currentSnapshot =
      createInitialSnapshot();

    const listeners =
      new Set<
        CanonicalTreeAuthenticatedEvidenceProductionObservationRegistryListener
      >();

    const notify =
      (): void => {
        for (const listener of [
          ...listeners,
        ]) {
          try {
            listener();
          } catch {
            // Diagnostic subscribers cannot interrupt production publication.
          }
        }
      };

    return Object.freeze({
      getSnapshot:
        () =>
          currentSnapshot,

      subscribe:
        listener => {
          listeners.add(
            listener,
          );

          let active =
            true;

          return () => {
            if (!active) {
              return;
            }

            active =
              false;

            listeners.delete(
              listener,
            );
          };
        },

      publishAccepted:
        observation => {
          const sanitized =
            validateObservation(
              observation,
            );

          currentSnapshot =
            Object.freeze({
              ...currentSnapshot,

              latestObservation:
                sanitized,

              observationCount:
                currentSnapshot.observationCount +
                1,

              revision:
                currentSnapshot.revision +
                1,

              changedAt:
                now(),
            });

          notify();

          return currentSnapshot;
        },

      clear:
        () => {
          if (
            currentSnapshot.latestObservation ===
              null &&
            currentSnapshot.observationCount ===
              0
          ) {
            return currentSnapshot;
          }

          currentSnapshot =
            Object.freeze({
              ...createInitialSnapshot(),

              revision:
                currentSnapshot.revision +
                1,

              changedAt:
                now(),
            });

          notify();

          return currentSnapshot;
        },
    });
  };

let canonicalRegistry:
  CanonicalTreeAuthenticatedEvidenceProductionObservationRegistry | null =
  null;

export const getCanonicalTreeAuthenticatedEvidenceProductionObservationRegistry =
  (): CanonicalTreeAuthenticatedEvidenceProductionObservationRegistry => {
    if (
      canonicalRegistry ===
        null
    ) {
      canonicalRegistry =
        createCanonicalTreeAuthenticatedEvidenceProductionObservationRegistry();
    }

    return canonicalRegistry;
  };

export const clearCanonicalTreeAuthenticatedEvidenceProductionObservationRegistryForTest =
  (): void => {
    canonicalRegistry =
      null;
  };

export const describeCanonicalTreeAuthenticatedEvidenceProductionObservationRegistry =
  () =>
    Object.freeze({
      milestone:
        'CC-TREE-ARCH-105',

      authority:
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_OBSERVABILITY_LIFECYCLE_RETIREMENT_READINESS_AUTHENTICATED_EVIDENCE_PRODUCTION_OBSERVATION_IMPLEMENTATION',

      decision:
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_OBSERVABILITY_LIFECYCLE_RETIREMENT_READINESS_AUTHENTICATED_EVIDENCE_PRODUCTION_OBSERVATION_REGISTRY_IMPLEMENTED',

      registry:
        'AUTHENTICATED_EVIDENCE_PRODUCTION_OBSERVATION_REGISTRY',

      scope:
        'TAB_MEMORY_ONLY',

      acceptedResultsOnly:
        true,

      skippedResultsStored:
        false,

      failedResultsStored:
        false,

      containsSecrets:
        false,

      containsAuthenticationData:
        false,

      containsOwnerIdentity:
        false,

      containsPrivateFamilyData:
        false,

      containsRawTreeData:
        false,

      containsRpcError:
        false,

      productionIngestionCertified:
        false,

      retirementAuthorized:
        false,
    });
